// Authoring helpers for multi-level (nested) detail forms.
//
// A child/sub-detail form must carry, as hidden key=true columns, every
// primary-key column of its IMMEDIATE parent (the foreign-key link the
// runtime fills via parent_pk on save), plus its own line_no discriminator.
// `makeChildForm` builds exactly that so a designer-added grandchild satisfies
// the runtime FK convention without relying on the AI generator.

import type { ColumnDef, JoinDef, SqlModel, TransactionModel } from './types'
import {
  allSqlModels, columnDbName, headerFormNo, parentFormNoOf, pkDbNames,
} from '@twasta/page-runtime/txn/utils'

// Next free numeric form_no (as a string), one past the current max.
export function nextFormNo(model: TransactionModel): string {
  const max = allSqlModels(model).reduce((mx, e) => {
    const n = parseInt(e.formNo, 10)
    return Number.isFinite(n) && n > mx ? n : mx
  }, 0)
  return String(max + 1)
}

// Next free child form_no for a given parent: the parent's form_no with a
// 1-based sequence appended, so the number encodes the hierarchy. E.g. the
// first child of form "3" is "31", the next "32"; the first child of "31" is
// "311". Skips any value already taken.
export function nextChildFormNo(
  model: TransactionModel, parentFormNo: string,
): string {
  const existing = new Set(allSqlModels(model).map(e => e.formNo))
  for (let k = 1; k < 1000; k++) {
    const candidate = `${parentFormNo}${k}`
    if (!existing.has(candidate)) return candidate
  }
  // Pathological fallback (1000 siblings) — keep it unique.
  return `${parentFormNo}_${allSqlModels(model).length + 1}`
}

// A fully-formed blank column matching the designer's "Add Column" default.
function blankColumn(
  name: string, dbName: string, colId: string, tableName: string,
): ColumnDef {
  return {
    name,
    col_id: colId,
    group_name: 'Basic Information',
    col_type: 'VARCHAR',
    heading: name,
    descr: '',
    key: false,
    default_value: '',
    protect: false,
    mandatory: false,
    db_name: dbName,
    table_name: tableName,
    db_size: '50',
    width: 200,
    x: '10',
    y: '50',
    hidden: false,
    format: '',
    edit_mask: { mask: '', values: [] },
    lookup: '',
    lookup_filter: '',
    item_change: '',
    validations: [],
  }
}

// The parent's primary-key ColumnDefs (so we can clone their type/size onto
// the child's hidden FK link columns).
function parentKeyColumns(parentSm: SqlModel): ColumnDef[] {
  return (parentSm.columns || [])
    .map(w => w?.column)
    .filter((c): c is ColumnDef =>
      !!c && (c.key === true || String(c.key).toLowerCase() === 'true'))
}

// Build a new child-form wrapper whose immediate parent is `parentFormNo`.
// Seeds: the parent's PK columns as hidden key=true FK columns (db_name +
// type copied so the link matches), a LINE_NO key discriminator, and a join
// back to the parent's table.
export function makeChildForm(
  model: TransactionModel, parentFormNo: string,
): { sql_model: SqlModel } {
  const entries = allSqlModels(model)
  const parentEntry = entries.find(e => e.formNo === parentFormNo)
  const parentSm = parentEntry?.sm
  const formNo = nextChildFormNo(model, parentFormNo)
  const objName = (model.obj_name || 'txn').toLowerCase().replace(/\s+/g, '_')
  const mainTable = `${objName}_d${formNo}`

  const columns: { column: ColumnDef }[] = []
  let y = 50
  let colId = 1
  for (const pc of parentKeyColumns(parentSm || ({} as SqlModel))) {
    const col = blankColumn(
      pc.name, columnDbName(pc), String(colId++), mainTable,
    )
    col.col_type = pc.col_type
    col.db_size = pc.db_size
    col.heading = pc.heading || pc.name
    col.key = true
    col.mandatory = 'true'
    col.hidden = '1'
    col.protect = true
    col.y = String(y)
    columns.push({ column: col })
    y += 40
  }
  // Own discriminator so the composite PK is unique per parent row. The
  // db_name/name are form-suffixed (line_no__f<formNo>) so they never collide
  // with a `line_no` inherited from the parent's own PK (which would otherwise
  // duplicate the column when nesting under a detail form).
  const lineNo = blankColumn(
    `LINE_NO__F${formNo}`, `line_no__f${formNo}`, String(colId++), mainTable,
  )
  lineNo.col_type = 'NUMBER'
  lineNo.db_size = '4'
  lineNo.heading = 'Line No'
  lineNo.key = true
  lineNo.mandatory = 'true'
  lineNo.y = String(y)
  columns.push({ column: lineNo })

  const parentTable = parentSm?.main_table || ''
  const fkNames = pkDbNames(parentSm || ({} as SqlModel))
  const joinList: JoinDef[] = [
    { table: mainTable, main_table: true, table_descr: '', column: '', join_column: '' },
  ]
  if (parentTable) {
    joinList.push({
      table: parentTable,
      main_table: false,
      table_descr: parentSm?.form_title || '',
      join_table: mainTable,
      join_type: 'inner',
      column: fkNames,
      join_column: fkNames,
    })
  }
  const joins = { join_predicates: { joins: joinList } }

  return {
    sql_model: {
      form_title: `Detail ${formNo}`,
      form_descr: '',
      form_no: formNo,
      parent_form_no: parentFormNo,
      main_table: mainTable,
      columns,
      actions: [],
      validations: [],
      joins,
    },
  }
}

// Display names of the IMMEDIATE parent form's columns for the form at index
// `fi` — the set a child's {header.X} lookup / item_change can bind against.
// Empty for the header (index 0). Uses parent_form_no, so a grandchild binds
// to its own parent (e.g. the Item line), not the root header.
export function parentColumnNames(
  model: TransactionModel, fi: number,
): string[] {
  if (fi === 0) return []
  const entries = allSqlModels(model)
  const entry = entries[fi]
  if (!entry) return []
  const parentNo = parentFormNoOf(entry, headerFormNo(model))
  const parent = entries.find(e => e.formNo === parentNo)?.sm
  return (parent?.columns || [])
    .map(c => c.column?.name || '')
    .filter(Boolean)
}

// db_names of the immediate-parent PK columns the child at index `fi` must
// carry as foreign keys.
function parentFkDbNames(model: TransactionModel, fi: number): string[] {
  const entries = allSqlModels(model)
  const entry = entries[fi]
  if (!entry) return []
  const parentNo = parentFormNoOf(entry, headerFormNo(model))
  const parent = entries.find(e => e.formNo === parentNo)?.sm
  return parent ? pkDbNames(parent) : []
}

// True when the child form at index `fi` already carries every one of its
// immediate parent's PK columns as a key column (the FK link). The header
// (index 0) is always considered covered.
export function pkColumnsCovered(model: TransactionModel, fi: number): boolean {
  if (fi === 0) return true
  const needed = parentFkDbNames(model, fi)
  if (needed.length === 0) return true
  const have = new Set(pkDbNames(allSqlModels(model)[fi].sm))
  return needed.every(n => have.has(n))
}

// Mutate `model` in place: prepend any of the immediate parent's PK columns
// that the child form at index `fi` is missing, as hidden key=true FK columns.
export function addMissingParentKeyColumns(
  model: TransactionModel, fi: number,
): void {
  if (fi === 0) return
  const entries = allSqlModels(model)
  const entry = entries[fi]
  if (!entry) return
  const parentNo = parentFormNoOf(entry, headerFormNo(model))
  const parentSm = entries.find(e => e.formNo === parentNo)?.sm
  if (!parentSm) return
  const childSm = model.sql_models[fi].sql_model
  if (!childSm.columns) childSm.columns = []
  const have = new Set(pkDbNames(childSm))
  const mainTable = childSm.main_table
  let colId = childSm.columns.length + 1
  let y = 50 + childSm.columns.length * 40
  // Prepend in reverse so the parent keys end up in declaration order at the
  // front of the form (the designer convention for FK link columns).
  for (const pc of [...parentKeyColumns(parentSm)].reverse()) {
    const dbn = columnDbName(pc)
    if (have.has(dbn)) continue
    const col = blankColumn(pc.name, dbn, String(colId++), mainTable)
    col.col_type = pc.col_type
    col.db_size = pc.db_size
    col.heading = pc.heading || pc.name
    col.key = true
    col.mandatory = 'true'
    col.hidden = '1'
    col.protect = true
    col.y = String(y)
    childSm.columns.unshift({ column: col })
    y += 40
  }
}

// A new column appended to a form — the designer's "Add column" default,
// placed at the end of `groupName` (the form's first group when absent) so
// it lands where the author clicked "+ Add column". Returns the new index.
export function appendColumn(sm: SqlModel, groupName?: string): number {
  const cols = sm.columns || (sm.columns = [])
  const ids = cols.map(c => parseInt(String(c.column?.col_id ?? ''), 10)).filter(Number.isFinite)
  const taken = new Set(cols.map(c => (c.column?.name || '').toUpperCase()))
  let n = Math.max(cols.length, ...ids, 0) + 1
  while (taken.has(`NEW_COLUMN_${n}`)) n += 1
  const group = (groupName ?? cols[0]?.column?.group_name ?? 'Basic Information') || 'Basic Information'
  const name = `NEW_COLUMN_${n}`
  const col = blankColumn(name, name, String(n), sm.main_table)
  col.heading = `New Column ${n}`
  col.group_name = group === 'Others' ? '' : group
  const inGroup = cols.filter(c => (c.column?.group_name || 'Others') === (group || 'Others'))
  const maxY = Math.max(0, ...inGroup.map(c => Number(c.column?.y) || 0), ...(inGroup.length ? [] : cols.map(c => Number(c.column?.y) || 0)))
  col.y = String(maxY + 100)
  const maxX = Math.max(0, ...cols.map(c => Number(c.column?.x) || 0))
  col.x = String(maxX + 10)
  cols.push({ column: col })
  return cols.length - 1
}

// A copy of a column placed right after it, with a free name.
export function duplicateColumn(sm: SqlModel, ci: number): number {
  const cols = sm.columns || []
  const src = cols[ci]?.column
  if (!src) return ci
  const copy: ColumnDef = JSON.parse(JSON.stringify(src))
  const taken = new Set(cols.map(c => (c.column?.name || '').toUpperCase()))
  let k = 2
  let name = `${src.name}_COPY`
  while (taken.has(name.toUpperCase())) name = `${src.name}_COPY${k++}`
  const linked = !src.db_name || src.db_name === src.name
  copy.name = name
  copy.db_name = linked ? name : `${src.db_name}_copy`
  copy.heading = `${src.heading || src.name} (copy)`
  copy.key = false
  copy.y = String((Number(src.y) || 0) + 1)
  const ids = cols.map(c => parseInt(String(c.column?.col_id ?? ''), 10)).filter(Number.isFinite)
  copy.col_id = String(Math.max(cols.length, ...ids, 0) + 1)
  cols.splice(ci + 1, 0, { column: copy })
  return ci + 1
}

// A bare form with no parent link — "Add form ▾ › Unlinked form (advanced)",
// the old object-root "Add New Form".
export function makeUnlinkedForm(model: TransactionModel): { sql_model: SqlModel } {
  const newNo = nextFormNo(model)
  return {
    sql_model: {
      form_title: `Form ${newNo}`,
      form_descr: '',
      form_no: newNo,
      main_table: '',
      columns: [],
      actions: [],
      validations: [],
      joins: { join_predicates: { joins: [] } },
    },
  }
}
